/**
 * GuideBookMetadataService - Manages guidebook metadata, categories, and color mappings
 * Handles loading guide/faq metadata, category colors, and lookups
 * 
 * @class GuideBookMetadataService
 */

import { UI_COLORS } from '../constants/index.js';

class GuideBookMetadataService {
    constructor() {
        /**
         * Category color mapping for UI styling
         * Maps category IDs to hex color objects with bgColor, borderColor, ringColor
         * @type {Object<string, Object>}
         */
        this.categoryColors = UI_COLORS.GUIDEBOOK_CATEGORIES;

        /**
         * Default color for unknown categories
         */
        this.defaultColor = UI_COLORS.DEFAULT_CATEGORY;

        /**
         * Metadata cache for loaded categories
         * Structure: { productId: { guide: [...], faq: [...] } }
         * @type {Object}
         */
        this.metadataCache = {};
    }

    /**
     * Get color object for a category
     * Returns hex color values for background, border, and ring
     * 
     * @param {string} categoryId - Category ID
     * @returns {Object} Color object with bgColor, borderColor, ringColor properties
     */
    getCategoryColor(categoryId) {
        return this.categoryColors[categoryId] || this.defaultColor;
    }

    /**
     * Get all category colors
     * 
     * @returns {Object} Category color mapping with hex color objects
     */
    getCategoryColors() {
        return { ...this.categoryColors };
    }

    /**
     * Set color for a category
     * 
     * @param {string} categoryId - Category ID
     * @param {Object} colorObject - Color object with bgColor, borderColor, ringColor properties
     */
    setCategoryColor(categoryId, colorObject) {
        this.categoryColors[categoryId] = colorObject;
    }

    /**
     * Cache metadata for a product
     * 
     * @param {string} productId - Product ID
     * @param {string} type - Type ('guide' or 'faq')
     * @param {Array} metadata - Metadata array
     */
    cacheMetadata(productId, type, metadata) {
        if (!this.metadataCache[productId]) {
            this.metadataCache[productId] = {};
        }
        this.metadataCache[productId][type] = metadata;
    }

    /**
     * Get cached metadata for a product
     * 
     * @param {string} productId - Product ID
     * @param {string} type - Type ('guide' or 'faq')
     * @returns {Array} Cached metadata or undefined
     */
    getCachedMetadata(productId, type) {
        const productMeta = this.metadataCache[productId];
        if (!productMeta) return undefined;
        return productMeta[type];
    }

    /**
     * Check if metadata is cached
     * 
     * @param {string} productId - Product ID
     * @param {string} type - Type ('guide' or 'faq')
     * @returns {boolean} True if cached
     */
    isMetadataCached(productId, type) {
        const cached = this.getCachedMetadata(productId, type);
        return cached !== undefined;
    }

    /**
     * Get category metadata by ID from a metadata array
     * 
     * @param {Array} metadataArray - Array of category metadata
     * @param {string} categoryId - Category ID to find
     * @returns {Object|null} Category metadata or null if not found
     */
    getCategoryMetadata(metadataArray, categoryId) {
        if (!metadataArray || !Array.isArray(metadataArray)) {
            return null;
        }
        const found = metadataArray.find(cat => cat.id === categoryId);
        return found || null;
    }

    /**
     * Transform metadata array to a map for faster lookups
     * 
     * @param {Array} metadataArray - Array of category metadata
     * @returns {Object} Map of id -> metadata
     */
    metadataArrayToMap(metadataArray) {
        if (!metadataArray || !Array.isArray(metadataArray)) {
            return {};
        }
        const map = {};
        for (const item of metadataArray) {
            if (item.id) {
                map[item.id] = item;
            }
        }
        return map;
    }

    /**
     * Get sorted categories by title
     * 
     * @param {Array} metadataArray - Array of category metadata
     * @returns {Array} Sorted categories
     */
    getSortedCategories(metadataArray) {
        if (!metadataArray || !Array.isArray(metadataArray)) {
            return [];
        }
        return [...metadataArray].sort((a, b) => {
            const titleA = (a.title || '').toLowerCase();
            const titleB = (b.title || '').toLowerCase();
            return titleA.localeCompare(titleB);
        });
    }

    /**
     * Clear all cached metadata
     */
    clearMetadataCache() {
        this.metadataCache = {};
    }

    /**
     * Get full metadata for a product (both guide and faq)
     * 
     * @param {string} productId - Product ID
     * @returns {Object} {guide: [...], faq: [...]} or null if not cached
     */
    getFullMetadata(productId) {
        const product = this.metadataCache[productId];
        if (!product) return null;
        return {
            guide: product.guide || [],
            faq: product.faq || []
        };
    }

    /**
     * Count total categories (guide + faq)
     * 
     * @param {string} productId - Product ID
     * @returns {number} Total category count
     */
    getCategoryCount(productId) {
        const full = this.getFullMetadata(productId);
        if (!full) return 0;
        return (full.guide?.length || 0) + (full.faq?.length || 0);
    }

    /**
     * Get metadata from persistent browser storage (localStorage)
     * Allows metadata to persist across page navigations and browser sessions
     * 
     * @param {string} storageKey - Key to retrieve from localStorage
     * @returns {Object|null} Persisted metadata object or null if not found/invalid
     */
    getPersistedMetadata(storageKey) {
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) return null;
            return JSON.parse(stored);
        } catch (error) {
            console.error(`Error reading persisted metadata from key "${storageKey}":`, error);
            return null;
        }
    }

    /**
     * Persist metadata to browser storage (localStorage)
     * Metadata survives page navigation and browser session restart
     * 
     * @param {string} storageKey - Key to store data under in localStorage
     * @param {Object} metadata - Metadata object with guides and faqs arrays
     * @returns {boolean} True if successfully persisted, false otherwise
     */
    persistMetadata(storageKey, metadata) {
        try {
            localStorage.setItem(storageKey, JSON.stringify(metadata));
            return true;
        } catch (error) {
            console.error(`Error persisting metadata to key "${storageKey}":`, error);
            return false;
        }
    }

    /**
     * Clear persisted metadata from browser storage
     * 
     * @param {string} storageKey - Key to remove from localStorage
     * @returns {boolean} True if successfully cleared, false otherwise
     */
    clearPersistedMetadata(storageKey) {
        try {
            localStorage.removeItem(storageKey);
            return true;
        } catch (error) {
            console.error(`Error clearing persisted metadata with key "${storageKey}":`, error);
            return false;
        }
    }
}

export default GuideBookMetadataService;
